import { useLocalization } from "@fluent/react";
import { LoadingEllipses } from "cerulean";
import { useEffect, useRef } from "react";
import { NavigationType, useBlocker } from "react-router";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useGetConsumerApplicationQuery } from "@lending/hooks/queries/useGetConsumerApplicationQuery";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import styles from "./Processing.module.css";
import { useStore } from "../../../../contexts/StoreContext";

const TERMINAL_STATES = [
  "opened",
  "rejected",
  "review",
  "opening_failed",
  "canceled",
  "expired",
] as const;

const POLL_INTERVAL_MS = 2000;

function Processing() {
  const navigate = useNavigateWithSearch();
  const { l10n } = useLocalization();
  const { data } = useStore();
  const { refetch } = useGetConsumerApplicationQuery(data.accountApplicationUuid);
  const isPolling = useRef(false);
  const isRequestInFlight = useRef(false);
  const refetchRef = useRef(refetch);

  useBlocker((navigation) => {
    return isPolling.current && navigation.historyAction === NavigationType.Pop;
  });

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (!data.accountApplicationUuid) {
      void navigate("../status");
      return;
    }

    isPolling.current = true;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      isPolling.current = false;
      isRequestInFlight.current = false;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const pollApplication = async () => {
      if (!isPolling.current || isRequestInFlight.current) {
        return;
      }

      isRequestInFlight.current = true;

      try {
        const { data: application } = await refetchRef.current();

        if (!isPolling.current) {
          return;
        }

        if (!application) {
          stopPolling();
          void navigate("../status", { replace: true });
          return;
        }

        if (application.state === "docs_requested") {
          stopPolling();
          void navigate("../documents-requested", { replace: true });
          return;
        }

        if (TERMINAL_STATES.includes(application.state as (typeof TERMINAL_STATES)[number])) {
          stopPolling();
          void navigate("../status", { replace: true });
        }
      } catch (error) {
        console.error("Error polling application:", error);
        stopPolling();
        void navigate("../status", { replace: true });
      } finally {
        isRequestInFlight.current = false;
      }
    };

    intervalId = setInterval(() => {
      void pollApplication();
    }, POLL_INTERVAL_MS);

    // Initial poll
    void pollApplication();

    return () => {
      stopPolling();
    };
  }, [data.accountApplicationUuid, navigate]);

  return (
    <PageLayout>
      <div className="alignChild--center--center margin--top--xxl">
        <div>
          <div
            className="alignChild--center--center margin--bottom--xl"
            style={{ position: "relative" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="150"
              height="150"
              viewBox="0 0 150 150"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M70.6645 0.0195305C106.349 -0.78658 138.977 23.4409 148.334 57.8864C156.608 88.3471 132.703 114.826 106.721 132.75C83.1586 149.004 52.9328 156.214 29.3807 139.945C4.19928 122.55 -5.56762 90.5215 3.09307 61.1673C12.5968 28.9559 37.0889 0.777998 70.6645 0.0195305Z"
                fill="var(--theme-primary)"
                fillOpacity="0.2"
              />
            </svg>
            <div className={styles.loadingEllipses}>
              <LoadingEllipses />
            </div>
          </div>
          <div className="alignChild--center--center" style={{ flexDirection: "column" }}>
            <div className="fontWeight--semibold fontSize--l margin--bottom--xl">
              {l10n.getString(
                "membership-share-processing-title",
                null,
                "Opening your membership account..."
              )}
            </div>
            <div>
              {l10n.getString(
                "membership-share-processing-message-1",
                null,
                "This can take up to a minute —"
              )}
            </div>
            <div>
              {l10n.getString(
                "membership-share-processing-message-2",
                null,
                "please do not close this window."
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export { Processing };
